import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Smartphone, ShieldCheck, Zap, Download, MessageCircle } from "lucide-react";
import { fadeUp } from "@/lib/motion";
import { WHATSAPP_URL } from "@/lib/filters";

const CELL =
    "relative overflow-hidden rounded-3xl border border-white/10 bg-surface p-6 sm:p-8 flex flex-col";

export default function Features() {
    return (
        <section id="features" className="py-20 sm:py-28 lg:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <motion.div {...fadeUp} className="max-w-2xl">
                    <p className="mb-4 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-amberx">
                        Why Deoeditz
                    </p>
                    <h2 className="font-display text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
                        Made for the phone in your hand.
                    </h2>
                </motion.div>

                <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-12">
                    <motion.div {...fadeUp} className={`${CELL} md:col-span-7`}>
                        <div className="relative z-10 max-w-sm">
                            <Smartphone className="mb-5 h-7 w-7 text-amberx" />
                            <h3 className="font-display text-2xl font-bold sm:text-3xl">Built for your thumb</h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">
                                Every control is a big, friendly touch target. Cut a whole reel with
                                one hand — on the bus, in a taxi queue, in the dark.
                            </p>
                        </div>
                        <div className="pointer-events-none absolute -bottom-6 -right-6 hidden w-52 rotate-3 overflow-hidden rounded-2xl border border-white/15 shadow-2xl sm:block">
                            <img
                                src="https://images.pexels.com/photos/7610521/pexels-photo-7610521.jpeg?auto=compress&cs=tinysrgb&w=600"
                                alt="Creator filming a skate clip on a phone"
                                className="h-full w-full object-cover"
                                style={{ filter: "sepia(30%) saturate(150%) brightness(105%) hue-rotate(-10deg)" }}
                                loading="lazy"
                            />
                        </div>
                    </motion.div>

                    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className={`${CELL} md:col-span-5`}>
                        <ShieldCheck className="mb-5 h-7 w-7 text-amberx" />
                        <h3 className="font-display text-2xl font-bold sm:text-3xl">Your clips stay yours</h3>
                        <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">
                            Everything is processed on your device, inside the browser. Nothing is
                            uploaded, nothing is stored, nothing leaks.
                        </p>
                    </motion.div>

                    <motion.div {...fadeUp} className={`${CELL} md:col-span-4`}>
                        <Zap className="mb-5 h-7 w-7 text-amberx" />
                        <h3 className="font-display text-2xl font-bold">Real-time engine</h3>
                        <p className="mt-3 text-sm leading-relaxed text-slate-400">
                            A canvas render loop paints every grade live while your video plays —
                            what you see is what you export.
                        </p>
                    </motion.div>

                    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className={`${CELL} md:col-span-4`}>
                        <Download className="mb-5 h-7 w-7 text-amberx" />
                        <h3 className="font-display text-2xl font-bold">Export &amp; post anywhere</h3>
                        <p className="mt-3 text-sm leading-relaxed text-slate-400">
                            Download the finished clip and drop it straight into Reels, TikTok or
                            WhatsApp Status.
                        </p>
                    </motion.div>

                    <motion.a
                        {...fadeUp}
                        transition={{ ...fadeUp.transition, delay: 0.16 }}
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="features-whatsapp-card"
                        className="relative overflow-hidden rounded-3xl border border-amberx/40 bg-amberx p-6 text-obsidian transition-transform hover:scale-[1.01] sm:p-8 md:col-span-4 flex flex-col"
                    >
                        <MessageCircle className="mb-5 h-7 w-7" />
                        <h3 className="font-display text-2xl font-bold">A human on WhatsApp</h3>
                        <p className="mt-3 text-sm font-medium leading-relaxed text-obsidian/80">
                            Stuck, or want a custom look for your brand? Message us and a real
                            editor replies.
                        </p>
                        <span className="mt-auto pt-5 font-mono text-[10px] font-bold tracking-[0.2em]">
                            +256 764 800 097 →
                        </span>
                    </motion.a>
                </div>
            </div>
        </section>
    );
}
